import { generateId } from '@ai-sdk/ui-utils';
import { isValidFigmaUrl } from '@animaapp/anima-sdk';
import { useStore } from '@nanostores/react';
import { useNavigate, useSearchParams } from '@remix-run/react';
import { formatDistanceToNow } from 'date-fns';
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast, type Id } from 'react-toastify';
import { LoginDialog } from './LoginDialog.client';
import { PreventAccidentalTabClose } from './PreventAccidentalTabClose';
import { StripeConfigurationDialog } from './stripe/StripeConfigurationDialog';
import { SupabaseConfigurationDialog } from './supabase/SupabaseConfigurationDialog';
import { BaseChat } from '~/components/chat/BaseChat';
import { DuplicateDialog } from '~/components/dialogs/DuplicateDialog';
import { DevicePreviewDialog } from '~/components/dialogs/device-preview-dialog';
import { RateLimitDialog } from '~/components/dialogs/rate-limit-dialog';
import { SwitchAccountDialog } from '~/components/dialogs/switch-account-dialog';
import { ConnectToFigmaDialog } from '~/components/figma/ConnectToFigmaDialog';
import { FigmaFrameDialog } from '~/components/figma/FigmaFrameDialog';
import { useFigmaImportScreen } from '~/components/figma/useFigmaImportScreen';
import { FileSizeToast } from '~/components/toasts/file-size';
import { Dialog, DialogActions, DialogButton, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import type { PreviewRef } from '~/components/workbench/Preview';
import { type IndexLoaderData } from '~/layouts/IndexLayout';
import { createEmptyProject, saveProject } from '~/lib/.client/api/projects';
import { clientFetch } from '~/lib/.client/fetch';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { sendAppSignalError } from '~/lib/appsignal.client';
import { classifyAttachments, saveAttachments } from '~/lib/attachments';
import { githubSyncMessage } from '~/lib/client-github';
import { BOLT_BOOTSTRAP_ID, BOLT_IMPORT_ID } from '~/lib/constants';
import {
  ensureConnectionToFigma,
  isFigmaRetry,
  redirectToFigmaImport,
  resetFigmaRetry,
  type FigmaFrame,
} from '~/lib/figma';
import { getFileSizeLimit } from '~/lib/file-limits';
import { useDragDrop, useMessages, usePromptEnhancer, useShortcuts } from '~/lib/hooks';
import { useGithubConflict } from '~/lib/hooks/useGithubConflict';
import { useGithubImportRepository } from '~/lib/hooks/useGithubImportRepository';
import { useGithubIntegration, type GithubIntegrationInitialState } from '~/lib/hooks/useGithubIntegration';
import { useGlobalInit } from '~/lib/hooks/useGlobalInit';
import { useInputWithHistoryStack } from '~/lib/hooks/useInputWithHistoryStack';
import { usePaymentIntentStatusMessage } from '~/lib/hooks/usePaymentIntentStatusMessage';
import { usePrevious } from '~/lib/hooks/usePrevious';
import { useProjects } from '~/lib/hooks/useProjects';
import { useSubscriptionActivateMessage } from '~/lib/hooks/useSubscriptionActivateMessage';
import { useThrottle } from '~/lib/hooks/useThrottle';
import { useTokenRefresh } from '~/lib/hooks/useTokenRefresh';
import { useLogin } from '~/lib/login';
import { init } from '~/lib/observers';
import { useChatHistory } from '~/lib/persistence/useChatHistory';
import {
  importStore,
  useGithubImport,
  useProjectImport,
  waitForProjectImportFiles,
  type ForkedProjectData,
} from '~/lib/projects';
import type { PromptMode } from '~/lib/prompts/types';
import { checkoutPreviousVersion } from '~/lib/snapshots/checkout';
import { chatStore } from '~/lib/stores/chat';
import { githubSyncs } from '~/lib/stores/github';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { projectStore } from '~/lib/stores/project';
import { stripeStore } from '~/lib/stores/stripe';
import { supabaseStore } from '~/lib/stores/supabase';
import { templateStore } from '~/lib/stores/template';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer } from '~/lib/webcontainer';
import { AnalyticsAction, AnalyticsTrackEvent, PricingPageOpenedTrigger } from '~/types/analytics';
import type { FileAttachment, Message } from '~/types/chat';
import type { OrganizationData } from '~/types/organization';
import { MAX_FILE_UPLOADS, SUPPORTED_PROMPT_FILE_TYPES_SET } from '~/utils/constants';
import { fromLatin1 } from '~/utils/encoding';
import { invariant } from '~/utils/invariant';
import { isBinaryFile } from '~/utils/is-binary-file';
import { createScopedLogger, renderLogger } from '~/utils/logger';
import { navigateToStackblitzProject } from '~/utils/navigate';
import { NOOP } from '~/utils/noops';
import { wait, withResolvers } from '~/utils/promises';
import { sanitizeStripeSecretKey } from '~/utils/stripe-helpers';
import { withSpinner } from '~/utils/withSpinner';
import '~/lib/eval-adapter';

const logger = createScopedLogger('Chat');

export function Chat() {
  renderLogger.trace('Chat');

  useChatHistory();

  return <ChatImpl legacyURLPrompt />;
}

export function ProjectImport({ slug, github }: { slug: string; github?: IndexLoaderData['github'] }) {
  renderLogger.trace('ProjectImport');

  useEffect(() => {
    projectStore.setSlug(slug);
  }, [slug]);

  const { chatHistory } = useProjectImport();

  useChatHistory({ chatHistory });

  useEffect(() => {
    if (!(github?.linked && !github.data)) {
      return NOOP;
    }

    let toastId: Id | undefined;

    const timerId = setTimeout(() => {
      toastId = toast.warn(
        `This project is linked to a private GitHub repo owned by someone else. Your changes won't sync to GitHub.`,
      );
    }, 1000);

    return () => {
      clearTimeout(timerId);
      toast.dismiss(toastId);
    };
  }, [github]);

  const githubData: GithubIntegrationInitialState | undefined =
    github?.linked && github.data ? { ...github.data, mode: 'project' } : undefined;

  useEffect(() => {
    if (githubData) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.GithubRepoCloneStarted,
        },
      });
    }
  }, []);

  return <ChatImpl initialChatStarted disableInput github={githubData} />;
}

export function GithubImport() {
  renderLogger.trace('GithubImport');

  const { requiresIntegration } = useGithubImport();

  useChatHistory();

  return <ChatImpl initialChatStarted disableInput github={requiresIntegration ? { mode: 'import' } : undefined} />;
}

export function FigmaImport({ connected }: { connected: boolean }) {
  const { loadingScreen } = useFigmaImportScreen(connected);

  useChatHistory();

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      // rendering the chat interface is kinda intensive, let's delay it
      setLoaded(true);
    }, 250);
  }, []);

  return (
    <>
      {loadingScreen}
      {loaded && <ChatImpl initialChatStarted disableInput />}
    </>
  );
}

const shortcutActions = {
  toggleTerminal: () => workbenchStore.toggleTerminal(),
};

function handleRateLimitOnUpgrade() {
  userStore.freeUserRateLimited.set(false);
  pricingStore.openPricingOverlay(PricingPageOpenedTrigger.RateLimitModal);
}

function handleRateLimitDialogClose() {
  userStore.freeUserRateLimited.set(false);
}

function handleSwitchAccount(organization: OrganizationData) {
  userStore.setActiveOrganization(organization, false);
  userStore.canSuggestSwitchAccount.set(false);
}

function handlSwitchAccountDialogClose() {
  userStore.canSuggestSwitchAccount.set(false);
}

function handlePromptModeChange(mode: PromptMode) {
  chatStore.promptMode.set(mode);
}

function updateLastRollbackInfo(rollbackedMessageId: string | undefined) {
  workbenchStore.lastRollbackedMessageId.set(rollbackedMessageId);
}

function resetRollbackInfo() {
  workbenchStore.rollbackMessageId.set(undefined);
}

interface ChatImplProps {
  legacyURLPrompt?: boolean;
  initialChatStarted?: boolean;
  disableInput?: boolean;
  github?: GithubIntegrationInitialState;
}

const githubInit = {
  webcontainer,
  async projectId() {
    await waitForProjectImportFiles();

    return projectStore.getProjectId();
  },
  fs: {
    writeFile(path: string, content: string | Uint8Array) {
      return workbenchStore.writeFile(path, {
        type: 'file',
        content,
        isBinary: ArrayBuffer.isView(content) && isBinaryFile(content),
      });
    },
    readFile(path: string) {
      try {
        const file = workbenchStore.readFile(path);
        return file.isBinary ? fromLatin1(file.content) : file.content;
      } catch {
        return undefined;
      }
    },
    removeFile(path: string) {
      return workbenchStore.removeFile(path);
    },
    ready: () => workbenchStore.waitForImport(),
  },
};

export const ChatImpl = memo((props: ChatImplProps) => {
  useGlobalInit();

  renderLogger.trace('ChatImpl');

  const { initialChatStarted, legacyURLPrompt, disableInput: initialDisableInput } = props;

  useShortcuts(shortcutActions);

  const previewRef = useRef<PreviewRef>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loginRequestRef = useRef<PromiseWithResolvers<boolean>>(null as any);
  const lastMessageContents = useRef<string | null>(null);

  const showChat = useStore(chatStore.showChat);
  const fileSelections = useStore(chatStore.fileSelections);
  const attachments = useStore(chatStore.attachments);
  const chatStarted = useStore(chatStore.started);
  const importInfo = useStore(importStore);
  const projectOrganization = useStore(projectStore.organization);
  const canSuggestSwitchAccount = useStore(userStore.canSuggestSwitchAccount);
  const requestStatus = useStore(chatStore.requestStatus);
  const rateLimitReached = useStore(userStore.rateLimitReached);
  const freeUserRateLimited = useStore(userStore.freeUserRateLimited);
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const isStreaming = useStore(workbenchStore.isStreaming);
  const showSupabaseProjectLimitReached = useStore(supabaseStore.showProjectLimitReached);
  const showProjectNameAlreadyExists = useStore(supabaseStore.showProjectNameAlreadyExists);
  const showSupabaseProjectConfiguration = useStore(supabaseStore.showSupabaseProjectConfiguration);
  const supabaseStatus = useStore(supabaseStore.supabaseStatus);
  const supabaseRequiresAction = useStore(supabaseStore.promptDisabled);
  const stripeDialogShown = useStore(stripeStore.isModalOpened);
  const promptMode = useStore(chatStore.promptMode);
  const loggedIn = useStore(userStore.loggedIn);
  const rollbackMessageId = useStore(workbenchStore.rollbackMessageId);

  const [loggingIn, setLoggingIn] = useState(false);
  const [connectingToFigma, setConnectingToFigma] = useState(false);
  const [choosingFigmaFrame, setChoosingFigmaFrame] = useState(() => isFigmaRetry());
  const [reverting, setReverting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [promptDisabled, setPromptDisabled] = useState(initialDisableInput);

  useTokenRefresh(loggedIn);

  useSubscriptionActivateMessage();
  usePaymentIntentStatusMessage();

  useEffect(() => {
    supabaseStore.init();
    init();

    workbenchStore.previewRef = previewRef;

    // stop stream when unloading
    return () => stop();
  }, []);

  if (loginRequestRef.current == null) {
    loginRequestRef.current = withResolvers();
  }

  useEffect(() => {
    resetFigmaRetry();
  }, []);

  const { login: directLogin } = useLogin();

  const login = useCallback(() => {
    if (loggedIn) {
      return true;
    }

    setLoggingIn(true);

    return loginRequestRef.current.promise;
  }, [loggedIn]);

  function cancelLogin() {
    setLoggingIn(false);
    loginRequestRef.current.resolve(false);
  }

  const { onForkProject, onOverwriteProject, conflict, conflictData } = useProjects();

  const {
    messages,
    input,
    handleInputChange: inputChangeCallback,
    reload,
    stop,
    acceptSupabaseChange,
    discardSupabaseChange,
    setMessages,
    setInput,
    append,
    showUserConfirmationDialog,
    acknowledgeAndContinue,
    closeUserConfirmation,
    reloadPreview,
    loadPreviousMessages,
    status: chatStatus,
  } = useMessages({
    previewRef,
    resetInput() {
      if (lastMessageContents.current) {
        setInput(lastMessageContents.current);
      } else {
        setInput('');
      }

      textareaRef.current?.focus();
    },
  });

  const setInputWithHistoryStack = useInputWithHistoryStack(textareaRef, inputChangeCallback);

  const [params] = useSearchParams();

  useEffect(() => {
    const status = importInfo.chatLoadingStatus;

    if (
      importInfo.messages.length > 0 &&
      (messages.findIndex((message) => message.id === BOLT_BOOTSTRAP_ID || message.id === BOLT_IMPORT_ID) === -1 ||
        status === 'LOADED')
    ) {
      if (status === 'LOADED') {
        // set to DONE so this action does not get repeated
        importStore.setKey('chatLoadingStatus', 'DONE');
      }

      setMessages([...importInfo.messages]);

      setPromptDisabled(false);

      const startPrompt = params.get('prompt');

      if (startPrompt) {
        sendMessage(startPrompt);
      }
    }
  }, [importInfo.messages, importInfo.chatLoadingStatus]);

  useEffect(() => {
    const startPrompt = params.get('prompt');

    if (legacyURLPrompt && startPrompt && !import.meta.hot?.data.appliedStartPrompt) {
      if (import.meta.hot) {
        import.meta.hot.data.appliedStartPrompt = true;
      }

      sendMessage(startPrompt);
    }
  }, []);

  useEffect(() => {
    if (rateLimitReached) {
      setPromptDisabled(true);
    }
  }, [rateLimitReached]);

  const github = useGithubIntegration(githubInit, props.github ?? { mode: 'link' }, chatStatus !== 'ready');
  const previousChatStatus = usePrevious(chatStatus);

  const lastAssistantMessageId = messages.findLast((message) => message.role === 'assistant')?.id;

  useEffect(() => {
    if (!github.connected()) {
      return;
    }

    const messageFinished = chatStatus === 'ready' && previousChatStatus === 'streaming';

    if (!messageFinished) {
      return;
    }

    if (lastAssistantMessageId) {
      const artifact = Object.values(workbenchStore.artifacts.get()).find(
        (artifact) => artifact.messageId === lastAssistantMessageId,
      );

      github.commit(artifact?.title).catch((error) => {
        logger.error(error);
        toast.error('Unable to commit to repository');
      });
    }
  }, [chatStatus, previousChatStatus, lastAssistantMessageId, github.commit, github.connected]);

  const messagesRef = useRef(messages);
  const sendMessageRef = useRef<typeof sendMessage>(NOOP);

  useEffect(() => {
    messagesRef.current = messages;
    sendMessageRef.current = sendMessage;
  });

  const githubConflict = useGithubConflict();

  useEffect(() => {
    const disposeUpdate = github.events.on('update', (event) => {
      const [oid, sync] = event.detail;

      setMessages((messages) => [...messages, githubSyncMessage(oid)]);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.GithubPull,
        },
      });

      githubSyncs.push({ oid, task: sync });
    });

    const disposeError = github.events.on('error', (event) => {
      toast.error(event.detail[0]);

      const error = event.detail[1];

      if (error) {
        sendAppSignalError(error);
      }
    });

    const disposeConflict = github.events.on('conflict', () => {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.GithubConflict,
        },
      });

      const currentMessages = messagesRef.current;
      const lastUserMessage = currentMessages.findLast((message) => message.role === 'user');

      setPromptDisabled(true);

      const done = githubConflict.show();

      async function solveConflict() {
        const tryAgain = await done.catch(NOOP as () => void);

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: tryAgain ? AnalyticsTrackEvent.GithubConflictRetried : AnalyticsTrackEvent.GithubConflictDiscarded,
          },
        });

        await github.pull();

        setPromptDisabled(false);

        if (tryAgain && lastUserMessage) {
          sendMessageRef.current(lastUserMessage.content);
        }
      }

      solveConflict();
    });

    return () => {
      disposeUpdate();
      disposeError();
      disposeConflict();
    };
  }, []);

  const maybePull = useThrottle(github.maybePull, 1000, { leading: true });

  const handleInputChange: typeof inputChangeCallback = useCallback(
    (event) => {
      if (github.connected()) {
        maybePull();
      }

      inputChangeCallback(event);
    },
    [inputChangeCallback, maybePull],
  );

  const { enhancingPrompt, promptEnhanced, enhancePrompt, resetEnhancer } = usePromptEnhancer();

  const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;

  const unloadHandler = useCallback(
    (event: BeforeUnloadEvent) => {
      // moved this to the top so it might work better

      if (!workbenchStore.hasPersistedChanges) {
        event.preventDefault();

        workbenchStore.saveAllFiles();
      }
    },
    [workbenchStore],
  );

  const navigate = useNavigate();
  const figmaState = useRef<FigmaFrame>();

  const startFigmaImport = useCallback(
    async (frame?: FigmaFrame) => {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.StartFromFigmaFrame,
          properties: {
            paste: frame != null,
          },
        },
      });

      const connected = loggedIn && (await ensureConnectionToFigma());

      if (connected) {
        if (frame) {
          redirectToFigmaImport(navigate, frame);
        } else {
          setChoosingFigmaFrame(true);
        }
      } else {
        if (!loggedIn) {
          if (!(await directLogin())) {
            return;
          }
        }

        figmaState.current = frame;
        setConnectingToFigma(true);
      }
    },
    [loggedIn, navigate],
  );

  function handleConnectedToFigma() {
    if (!connectingToFigma) {
      return;
    }

    setConnectingToFigma(false);

    if (figmaState.current) {
      const frame = figmaState.current;
      figmaState.current = undefined;

      redirectToFigmaImport(navigate, frame);
    } else {
      setChoosingFigmaFrame(true);
    }
  }

  async function importFigmaFrame(frame?: FigmaFrame) {
    if (!frame) {
      setChoosingFigmaFrame(false);
      return;
    }

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.ImportFigmaFrame,
      },
    });

    redirectToFigmaImport(navigate, frame);

    // this is a spurious wait to ease the page transition
    await wait(3_000);

    setChoosingFigmaFrame(false);
  }

  const tier = useStore(userStore.getTierStore());
  const isPaidUser = useStore(userStore.isPaidUser);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!(await login())) {
        return;
      }

      if (chatStore.attachments.get().length >= MAX_FILE_UPLOADS) {
        toast.error(`You can only upload a maximum of ${MAX_FILE_UPLOADS} files`);
        return;
      }

      const fileSizeLimit = getFileSizeLimit({ tier, target: 'bolt' });

      if (file.size > fileSizeLimit.bytes) {
        toast.error(() => <FileSizeToast limit={fileSizeLimit.mb} isPaidUser={isPaidUser} />);
        return;
      }

      const attachment = chatStore.addAttachment({ file, uploading: true });

      try {
        const formData = new FormData();

        formData.append('file', file);

        await withSpinner({
          action: async () => {
            const response = await clientFetch('/api/upload', {
              method: 'POST',
              body: formData,
            });

            if (!response.ok) {
              throw new Error('Failed to upload file');
            }

            const { key } = await response.json<{ key: string }>();

            chatStore.updateAttachment(attachment.id, { storageKey: key });
          },
          onStateUpdate: (isSpinning) => {
            if (!isSpinning) {
              chatStore.updateAttachment(attachment.id, { uploading: false });
            }
          },
          spinnerDelay: 0,
          minDuration: 200,
        });
      } catch (error) {
        chatStore.removeAttachment(attachment);

        logger.error(error);

        toast.error('Failed to upload file');
      }
    },
    [loggedIn],
  );

  useEffect(() => {
    return workbenchStore.inspectedElement.subscribe((inspectedElement, previousInspectedElement) => {
      if (
        typeof previousInspectedElement !== 'undefined' &&
        inspectedElement &&
        previousInspectedElement?.file !== inspectedElement.file
      ) {
        uploadFile(inspectedElement.file);
        textareaRef.current?.focus();
      }
    });
  }, [uploadFile]);

  useEffect(() => {
    chatStore.started.set(!!initialChatStarted);
  }, []);

  const { isDragging, handleDrop } = useDragDrop(uploadFile, { allowedExtensions: SUPPORTED_PROMPT_FILE_TYPES_SET });

  useLayoutEffect(() => {
    const unsubscribeFromFileSelections = chatStore.fileSelections.subscribe(() => {
      textareaRef.current?.focus();
    });

    window.addEventListener('beforeunload', unloadHandler);

    return () => {
      unsubscribeFromFileSelections();

      window.removeEventListener('beforeunload', unloadHandler);
    };
  }, []);

  const handleFileSelectionRemove = useCallback((index: number) => {
    chatStore.removeFileSelection(index);
  }, []);

  const handleAttachmentRemove = useCallback((attachment: FileAttachment) => {
    chatStore.removeAttachment(attachment);
  }, []);

  const scrollTextArea = () => {
    const textarea = textareaRef.current;

    if (textarea) {
      textarea.scrollTop = textarea.scrollHeight;
    }
  };

  const abort = useCallback(() => {
    stop('aborted-by-user');
    chatStore.aborted.set(true);
    workbenchStore.abortAllActions();
  }, [stop]);

  const onRollback = useCallback((message: Message) => {
    workbenchStore.rollbackMessageId.set(message.id);
  }, []);

  const handleRollback = () => {
    if (!rollbackMessageId) {
      return;
    }

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.UserRollbackToPreviousMessageStarted,
      },
    });

    updateLastRollbackInfo(undefined);

    withSpinner({
      action: async () => {
        const rollbackMessage = messages.find(({ id }) => id === rollbackMessageId);

        if (!rollbackMessage) {
          return;
        }

        try {
          // the messageId will be set in case of an import complete message
          await checkoutPreviousVersion(rollbackMessage.messageId ?? rollbackMessage.id);

          setMessages((messages) => {
            const newMessages = [];

            for (const message of messages) {
              newMessages.push(message);

              if (message.id === rollbackMessage.id) {
                break;
              }
            }

            return newMessages;
          });

          updateLastRollbackInfo(rollbackMessageId);
          resetRollbackInfo();

          reloadPreview();

          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.UserRollbackToPreviousMessageSuccess,
            },
          });

          toast.success('Successfully reverted changes');
        } catch (error) {
          logger.error(error);

          toast.error('Failed to revert changes');

          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.UserRollbackToPreviousMessageFailure,
            },
          });
        }
      },
      onStateUpdate: setReverting,
    });
  };

  useLayoutEffect(() => {
    const textarea = textareaRef.current;

    if (textarea) {
      textarea.style.height = 'auto';

      const scrollHeight = textarea.scrollHeight;

      textarea.style.height = `${Math.min(scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
      textarea.style.overflowY = scrollHeight > TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
    }
  }, [input, textareaRef]);

  const typeMessage = async (message: string) => {
    if (isTyping) {
      return false;
    }

    setIsTyping(true);

    setInput('');

    for (let i = 0; i < message.length; i++) {
      setInput((prevInput) => prevInput + message[i]);
      await wait(20);
    }

    setIsTyping(false);

    return true;
  };

  const checkForFigmaUrl = (content: string) => {
    const trimmed = content.trim();
    const [isValid, fileKey, nodeId] = isValidFigmaUrl(trimmed);

    if (isValid && nodeId) {
      startFigmaImport({ fileKey, nodeId, url: trimmed });

      return true;
    }

    return false;
  };

  const analyzeAttachments = async (message: Message, onComplete: () => void) => {
    try {
      const classifyResult = await classifyAttachments(message, attachments);

      const proceed = classifyResult.proceed;

      const saveAttachmentsResult = await saveAttachments(classifyResult.files);

      if (saveAttachmentsResult) {
        if (saveAttachmentsResult.renamedFiles.size > 0) {
          for (const [oldFileName, newFileName] of saveAttachmentsResult.renamedFiles.entries()) {
            for (const attachment of message.experimental_attachments ?? []) {
              invariant(attachment.name);

              if (attachment.name === oldFileName) {
                attachment.name = newFileName;
              }
            }
          }
        }

        setMessages((currentMessages) => {
          return [...currentMessages, ...saveAttachmentsResult.newMessages];
        });
      }

      if (proceed && !chatStore.aborted.get()) {
        onComplete();
      } else {
        workbenchStore.isStreaming.set(false);
      }
    } catch (error: any) {
      logger.error(error);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.AnalyzeAttachmentsFailed,
          properties: {
            error: `${error}`,
            stack: error.stack,
          },
        },
      });

      if (!chatStore.aborted.get()) {
        onComplete();
      }
    }
  };

  const sendMessage = async (
    content = input,
    options?: {
      details?: Record<string, unknown>;
    },
  ) => {
    if (!(await login())) {
      return false;
    }

    const generatedMessageId: string = generateId();

    let creatingProject: Promise<ForkedProjectData | undefined> | undefined;
    let isForking: Promise<unknown> | undefined;

    if (projectStore.shouldFork() && !projectStore.isForking.get()) {
      projectStore.isForking.set(true);

      isForking = onForkProject();
    }

    if (content.length === 0 || isStreaming) {
      return false;
    }

    const [newContent, stripeKeys] = sanitizeStripeSecretKey(content);

    content = newContent;

    chatStore.aborted.set(false);

    let fetchesTemplate = false;

    if (!chatStarted) {
      if (checkForFigmaUrl(input)) {
        return false;
      }

      if (import.meta.env.PROD || import.meta.env.VITE_DISABLE_PROJECTS !== 'true') {
        let retries = 0;

        const maxRetries = 2;

        const attemptCreateProject = async (): Promise<ForkedProjectData | undefined> => {
          return createEmptyProject(activeOrganization)
            .then((project) => {
              projectStore.updateProject(project, userStore.getActiveOrganization().get());
              navigateToStackblitzProject(project.slug);

              return project;
            })
            .catch((error: Error) => {
              if (retries < maxRetries) {
                retries++;

                return attemptCreateProject();
              }

              logger.error(error);
              toast.error('Failed to create project');

              return undefined;
            });
        };

        creatingProject = attemptCreateProject();
      }

      fetchesTemplate = true;

      const abortController = new AbortController();

      const unsubscribeFromAborted = chatStore.aborted.subscribe((aborted) => {
        if (aborted) {
          workbenchStore.isStreaming.set(false);
          abortController.abort('Streaming aborted');
        }
      });

      // we don't have to await this call because it will be picked up by the ImportTemplateAction
      Promise.all([
        templateStore.tryFetchTemplate(content, abortController.signal, generatedMessageId),
        creatingProject,
      ])
        .then(([template, project]) => {
          if (template && project) {
            const files = workbenchStore.files.get();

            return saveProject({
              slug: project.slug,
              editedAt: project.editedAt,
              files,
            });
          }

          return undefined;
        })
        .then((response) => {
          if (response) {
            projectStore.setEditedAt(response.editedAt);
          }
        })
        .finally(() => {
          unsubscribeFromAborted();
        });
    }

    // project forking must finish before we continue making changes to the project, e.g. saving files or chat history
    if (isForking) {
      workbenchStore.isStreaming.set(true);

      await isForking;
    }

    /**
     * @note (delm) Usually saving files shouldn't take long but it may take longer if there
     * many unsaved files. In that case we need to block user input and show an indicator
     * of some kind so the user is aware that something is happening. But I consider the
     * happy case to be no unsaved files and I would expect users to save their changes
     * before they send another message.
     */
    await workbenchStore.saveAllFiles();

    chatStore.started.set(true);

    if (fileSelections.length > 0) {
      chatStore.resetFileSelections();
    }

    const targetedFiles = Object.keys(workbenchStore.targetedFiles.get());

    const lockedFiles = Object.keys({
      ...workbenchStore.lockedFiles.get(),

      // migration files should always be seen as locked files by the LLM to prevent it from modifying them if we send them as context
      ...(supabaseStore.isConfigured.get() ? {} : workbenchStore.migrationFiles.get()),
    });

    let focusedFiles;

    if (lockedFiles.length > 0 || targetedFiles.length > 0) {
      focusedFiles = {
        lockedFiles,
        targetedFiles,
      };
    }

    const message: Message = {
      type: 'chat',
      id: generatedMessageId,
      status: fetchesTemplate ? 'complete' : 'streaming',
      role: 'user',
      input: content,
      content,
      ...(stripeKeys.size > 0
        ? {
            stripeIntegration: {
              phase: 'api-key-change',
              secretKeys: [...stripeKeys],
            },
          }
        : {}),
      experimental_attachments: attachments.map(({ file, storageKey }) => {
        return {
          name: file.name,
          contentType: file.type,
          url: storageKey,
        };
      }),
      details: options?.details,
      fileSelections,
      focusedFiles,
      parts: [],
    };

    workbenchStore.isStreaming.set(true);

    if (!fetchesTemplate) {
      append({ ...message, status: 'complete' }, false).then(() => analyzeAttachments(message, () => reload()));
    } else {
      setMessages(() => {
        return [message];
      });

      invariant(templateStore.ready);

      templateStore.ready.then(() => analyzeAttachments(message, () => reload()));
    }

    chatStore.resetAttachments();

    setInput('');

    lastMessageContents.current = content;
    textareaRef.current?.focus();

    resetEnhancer();

    textareaRef.current?.blur();

    if (messages.length === 0) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.ChatCreated,
          properties: {
            message: content,
          },
        },
      });
    }

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.MessageSent,
        properties: {
          message: content,
          messageId: message.id,
          promptMode: chatStore.promptMode.get(),
        },
      },
    });

    return true;
  };

  const onRetry = () => {
    if (textareaRef.current) {
      chatStore.requestStatus.set('PENDING');

      const enterKeyEvent = new KeyboardEvent('keydown', {
        key: 'Enter',
        code: 'Enter',
        which: 13,
        keyCode: 13,
        bubbles: true,
        cancelable: true,
      });

      textareaRef.current.dispatchEvent(enterKeyEvent);
    }
  };

  useEffect(() => {
    const unsubscribe = chatStore.events.on('sendChatMessage', ({ detail }) => {
      const { message, mode, details } = detail;

      if (mode) {
        chatStore.promptMode.set(mode);
      }

      sendMessage(message, { details });
    });

    return () => {
      unsubscribe();
    };
  }, [sendMessage]);

  function handleLoginSuccess() {
    setLoggingIn(false);
    loginRequestRef.current.resolve(true);
  }

  const githubImport = useGithubImportRepository(directLogin);

  return (
    <>
      <BaseChat
        textareaRef={textareaRef}
        input={input}
        showChat={showChat}
        chatStarted={chatStarted}
        isStreaming={isStreaming}
        isDragging={isDragging}
        promptDisabled={promptDisabled || supabaseRequiresAction}
        enhancingPrompt={enhancingPrompt}
        promptEnhanced={promptEnhanced}
        fileSelections={fileSelections}
        attachments={attachments}
        acceptSupabaseChange={acceptSupabaseChange}
        discardSupabaseChange={discardSupabaseChange}
        onRollback={onRollback}
        sendMessage={sendMessage}
        typeMessage={typeMessage}
        handleDrop={handleDrop}
        handleInputChange={handleInputChange}
        login={login}
        onRemoveFileSelection={handleFileSelectionRemove}
        onRemoveAttachment={handleAttachmentRemove}
        onUploadFile={uploadFile}
        onImportFigma={startFigmaImport}
        onImportGithub={githubImport.startImportRepository}
        handleStop={abort}
        messages={messages}
        previewRef={previewRef}
        mode={promptMode}
        loadMoreMessages={loadPreviousMessages}
        onModeChange={handlePromptModeChange}
        enhancePrompt={async () => {
          if (!(await login())) {
            return;
          }

          enhancePrompt(
            input,
            (input) => {
              setInput(input);
              scrollTextArea();
            },
            ({ finalInput, originalInput }) => {
              setInput('');
              setTimeout(() => {
                setInputWithHistoryStack(originalInput);
                setInputWithHistoryStack(finalInput);
              });
              scrollTextArea();
            },
          );
        }}
        resubmit={(failedMessage) => {
          if (lastMessageContents.current === input) {
            setInput('');
          }

          if (failedMessage.error?.code === 'free-user-rate-limited') {
            // user is retrying so we need to send the event
            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.BoltConcurrentFreeUsersRetry,
              },
            });
          }

          try {
            setMessages((messages) => {
              const newMessages = [...messages];

              invariant(failedMessage.role !== 'assistant', 'Only user messages can be retried');

              const failedMessageIndex = messages.findLastIndex((message) => {
                if (message.id === failedMessage.id) {
                  return true;
                }

                // if we find a non-ignored message which is not the failed message, we don't want to do anything
                invariant(message.ignore, 'Retry message should be last');

                return false;
              });

              newMessages[failedMessageIndex] = {
                ...failedMessage,
                ignore: false,
                error: undefined,
              };

              return newMessages;
            });
          } catch {
            // do nothing
          }

          reload();
        }}
      />

      <LoginDialog open={loggingIn} onClose={cancelLogin} onLoginSuccess={handleLoginSuccess} />

      <Dialog open={conflict} maxWidth={600} showCloseButton={false}>
        <DialogTitle>Saving could overwrite a more recent version</DialogTitle>
        <DialogDescription>
          <p>This project was updated recently from another device or in another browser tab.</p>
          {conflictData && (
            <p className="mt-1">
              Last saved by <span className="font-semibold"> {conflictData.lastEditor}</span>{' '}
              {formatDistanceToNow(conflictData.editedAt, {
                addSuffix: true,
              })}
              .
            </p>
          )}
        </DialogDescription>
        <DialogActions>
          <DialogButton variant="secondary" disabled={false} onClick={onForkProject}>
            Fork this project
          </DialogButton>
          <DialogButton variant="danger" onClick={onOverwriteProject}>
            Save and overwrite existing project
          </DialogButton>
        </DialogActions>
      </Dialog>

      <DuplicateDialog />

      <Dialog open={showUserConfirmationDialog} onClose={closeUserConfirmation} maxWidth={600}>
        <DialogTitle>Large Project Size Alert</DialogTitle>
        <DialogDescription>
          <p className="mb-4">
            This project seems to be quite large in size, which increases token usage per message. To learn how to
            reduce token consumption and optimize your project{' '}
            <a
              className="underline text-bolt-elements-link hover:text-bolt-elements-linkHover"
              target="_blank"
              href="https://bolt.fyi/large-projects"
            >
              visit our guide.
            </a>
          </p>
        </DialogDescription>
        <DialogActions>
          <DialogButton variant="secondary" onClick={closeUserConfirmation}>
            Cancel
          </DialogButton>
          <DialogButton variant="warn" onClick={acknowledgeAndContinue}>
            Send Message
          </DialogButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rollbackMessageId}
        maxWidth={600}
        showCloseButton={false}
        closeOnBackdropClick={!reverting}
        onClose={resetRollbackInfo}
      >
        <DialogTitle>Revert to Previous Version</DialogTitle>
        <DialogDescription>
          <p>You are about to revert your project to a previous state.</p>
          <p className="my-6 bg-alpha-orange-10 border-l border-l-2 border-orange-500 p-2 px-4 text-orange-600 dark:text-orange-500">
            This action will undo all changes made since the selected version. Your current work will be lost and cannot
            be recovered.
          </p>
          <p>Are you sure you want to proceed?</p>
        </DialogDescription>
        <DialogActions>
          <DialogButton variant="secondary" disabled={reverting} onClick={resetRollbackInfo}>
            Cancel
          </DialogButton>
          <DialogButton variant="warn" disabled={reverting} minWidth={30} onClick={handleRollback}>
            {reverting ? (
              <div className="flex items-center gap-1.5">
                <div className="i-svg-spinners:90-ring-with-bg text-lg" />
                Reverting
              </div>
            ) : (
              'Revert now'
            )}
          </DialogButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSupabaseProjectLimitReached}
        maxWidth={600}
        showCloseButton={true}
        closeOnBackdropClick={true}
        onClose={() => supabaseStore.showProjectLimitReached.set(false)}
      >
        <DialogTitle>Supabase project limit reached</DialogTitle>
        <DialogDescription>
          <p>
            It seems like you have hit the limit for the amount of free active projects you can create on your account.{' '}
            <a href="https://supabase.com/dashboard/projects" target="_blank">
              Visit the Supabase dashboard
            </a>{' '}
            and pause or delete an existing project and try again.
          </p>
        </DialogDescription>

        <DialogActions>
          <DialogButton variant="secondary" onClick={() => supabaseStore.showProjectLimitReached.set(false)}>
            Cancel
          </DialogButton>
          <DialogButton
            variant="primary"
            onClick={() => {
              supabaseStore.showProjectLimitReached.set(false);

              supabaseStore.showSupabaseCreateNewProjectConfiguration();
            }}
          >
            Create
          </DialogButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showProjectNameAlreadyExists}
        maxWidth={600}
        showCloseButton={true}
        closeOnBackdropClick={true}
        onClose={() => supabaseStore.showProjectNameAlreadyExists.set(false)}
      >
        <DialogTitle>Supabase project name already exists</DialogTitle>
        <DialogDescription>
          <p>The project name you chose already exists. Please choose another name.</p>
        </DialogDescription>

        <DialogActions>
          <DialogButton variant="secondary" onClick={() => supabaseStore.showProjectNameAlreadyExists.set(false)}>
            Cancel
          </DialogButton>
          <DialogButton
            variant="primary"
            onClick={() => {
              supabaseStore.showProjectNameAlreadyExists.set(false);

              supabaseStore.showSupabaseCreateNewProjectConfiguration();
            }}
          >
            Create a new project
          </DialogButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={supabaseStatus === 'REMOVED'}
        maxWidth={600}
        showCloseButton={true}
        closeOnBackdropClick={true}
        onClose={() => supabaseStore.supabaseStatus.set('NOT_CONFIGURED')}
      >
        <DialogTitle>Supabase project removed</DialogTitle>
        <DialogDescription>
          <p>We can't find your project anymore. It seems like it was removed. You can connect to a new project.</p>
        </DialogDescription>

        <DialogActions>
          <DialogButton
            variant="secondary"
            onClick={() => {
              supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
            }}
          >
            Close
          </DialogButton>
          <DialogButton
            variant="primary"
            onClick={() => {
              supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
              supabaseStore.showSupabaseCreateNewProjectConfiguration();
            }}
          >
            Create
          </DialogButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={supabaseStatus === 'UNHEALTHY'}
        maxWidth={600}
        showCloseButton={true}
        closeOnBackdropClick={true}
        onClose={() => supabaseStore.supabaseStatus.set('NOT_CONFIGURED')}
      >
        <DialogTitle>Supabase project not active</DialogTitle>
        <DialogDescription>
          <p>
            This project is not active. It might be paused. Click on 'Connect To Supabase' where you can unpause the
            project and reconnect.
          </p>
        </DialogDescription>

        <DialogActions>
          <DialogButton
            variant="secondary"
            onClick={() => {
              supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
            }}
          >
            Close
          </DialogButton>
        </DialogActions>
      </Dialog>

      <SupabaseConfigurationDialog open={showSupabaseProjectConfiguration} />

      <StripeConfigurationDialog open={stripeDialogShown} />

      <RateLimitDialog
        open={(requestStatus === 'ERROR' || requestStatus === 'PENDING') && freeUserRateLimited}
        onRetry={onRetry}
        onUpgrade={handleRateLimitOnUpgrade}
        onClose={handleRateLimitDialogClose}
        requestStatus={requestStatus}
      />

      <SwitchAccountDialog
        open={canSuggestSwitchAccount}
        onSwitch={handleSwitchAccount}
        onClose={handlSwitchAccountDialogClose}
        projectOrganization={projectOrganization}
        activeOrganization={activeOrganization}
      />

      <DevicePreviewDialog />

      <ConnectToFigmaDialog
        onSuccess={handleConnectedToFigma}
        onClose={() => {
          figmaState.current = undefined;
          setConnectingToFigma(false);
        }}
        open={connectingToFigma}
      />

      <FigmaFrameDialog open={choosingFigmaFrame} onSubmit={importFigmaFrame} />

      <PreventAccidentalTabClose />

      {githubImport.dialog}

      {githubConflict.dialog}
    </>
  );
});
